import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdminStore } from '../../stores/adminStore';
import { useTranslation } from '../../hooks/useTranslation';
import { formatDistanceToNow } from 'date-fns';
import { JsonViewer } from '../../utils/adminFunctions';
import { PaymentStatusBadge } from '../../components/PaymentStatus';
import { LocalizedLink } from '../../components/LocalizedLink';

export const InvoiceDetails: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { uid, invoiceId } = useParams<{ uid: string; invoiceId: string }>();
  const { 
    selectedUser, 
    userInvoices, 
    userInvoicesLoading,
    fetchUserInvoices 
  } = useAdminStore();

  const invoice = userInvoices.find(inv => inv.id === invoiceId);

  useEffect(() => {
    if (!uid || !invoiceId) {
      navigate('/admin');
      return;
    }
    
    if (userInvoices.length === 0) {
      fetchUserInvoices(uid);
    }
  }, [uid, invoiceId, userInvoices.length, navigate, fetchUserInvoices]);

  const handleRefresh = async () => {
    if (uid) {
      await fetchUserInvoices(uid);
    }
  };

  if (!invoice || !selectedUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50/50 backdrop-blur-xl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Ensure selectedUser has settings to prevent errors
  if (!selectedUser.settings) {
    console.log('[InvoiceDetails] selectedUser.settings is missing, adding empty object');
    selectedUser.settings = {
      language: 'en',
      country: 'NL',
      invoiceStartNumber: 1,
      latestInvoiceNumber: 0,
      invoiceNumberFormat: {
        prefix: 'INV',
        includeYear: true,
        yearFormat: 'full',
        separator: '-',
        numberPadding: 4
      }
    };
  }

  // Get last modified date from metadata or fallback to current date
  const lastModified = (invoice as any).updatedAt || (invoice as any).createdAt || new Date().toISOString();

  return (
    <div className="min-h-screen bg-gray-50/50 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <LocalizedLink
              to={`/admin/user/${uid}/invoices`}
              className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-2"
            >
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('admin.backToInvoices')}
            </LocalizedLink>
            <h1 className="text-2xl font-semibold text-gray-900">
              {t('admin.invoiceDetails.title', { number: invoice.invoiceNumber })}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-sm text-gray-500">
                {t('admin.invoiceDetails.subtitle', { email: selectedUser.email })}
              </p>
              <span className="text-gray-300">•</span>
              <p className="text-sm text-gray-500">
                {t('admin.lastChanged', { time: formatDistanceToNow(new Date(lastModified), { addSuffix: true }) })}
              </p>
            </div>
          </div>
          <div className="flex space-x-3">
            <LocalizedLink
              to={`/admin/user/${uid}/companies`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {t('admin.users.viewCompanies')}
            </LocalizedLink>
            <LocalizedLink
              to={`/admin/user/${uid}/settings`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {t('admin.users.viewSettings')}
            </LocalizedLink>
            <button
              onClick={handleRefresh}
              disabled={userInvoicesLoading}
              className={`inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-150 ${
                userInvoicesLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <svg 
                className={`w-4 h-4 mr-2 ${userInvoicesLoading ? 'animate-spin' : ''}`} 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                />
              </svg>
              {userInvoicesLoading ? t('common.refreshing') : t('common.refresh')}
            </button>
          </div>
        </div>

        {/* Invoice Data Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-black/5 p-6">
          <div className="space-y-6">
            {/* Quick Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pb-6 border-b border-gray-100">
              <div>
                <div className="text-sm font-medium text-gray-500 mb-1">{t('invoice.number')}</div>
                <div className="text-lg font-medium text-gray-900">{invoice.invoiceNumber}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500 mb-1">{t('invoice.date')}</div>
                <div className="text-lg text-gray-900">
                 {invoice.date}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500 mb-1">{t('invoice.to')}</div>
                <div className="text-lg text-gray-900">{invoice.recipient.company}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500 mb-1">&nbsp;</div>
                <PaymentStatusBadge status={invoice.paymentStatus} asChild />
              </div>
            </div>

            {/* Raw Data */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-4">{t('admin.invoiceDetails.rawData')}</h3>
              <JsonViewer data={invoice} language="en" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 